<template>
  <WdsModal :show="true" :title="$t('Override Classification')" @cancel="$emit('close')">
    <WdsSelect
      v-model="overrideResultSelect"
      :label="$t('Change to') + ' *'"
      :list="[{ value: 'outside', label: $t('Independent Contractor') }]"
      required
    />

    <div class="tw-mt-4">
      <WdsInputArea
        v-model="overrideReason"
        :label="$t('Reason for Override') + ' *'"
        :placeholder="
          $t(
            'Please provide a detailed reason for this override. This will be visible to the freelancer.',
          )
        "
        required
      />
    </div>

    <ContractFormSignature
      ref="signatureComponent"
      :disabled="false"
      :initial-signature="''"
      :is-signed="false"
    />

    <WdsAlert warning>
      <template #description>
        <WdsCheckbox v-model:checked="overrideAccept" required>
          <div>
            <WdsText class="tw-mb-1">
              {{ $t('Liability Acknowledgment (required)') }}
            </WdsText>
            <WdsText>
              {{
                $t(
                  'I confirm that this classification is based on facts provided by me or my organization. I understand that by overriding this recommendation, the liability for any misclassification falls to me.',
                )
              }}
            </WdsText>
          </div>
        </WdsCheckbox>
      </template>
    </WdsAlert>

    <template #footer>
      <form :action="overrideUrl" method="POST" class="tw-w-full" @submit="handleSubmit">
        <WdsStack direction-row gap-sm justify-end class="tw-w-full">
          <WdsButton @click="$emit('close')">
            {{ $t('Cancel') }}
          </WdsButton>

          <WdsButton
            type="submit"
            primary
            :disabled="submitting || !canSubmit"
            :loading="submitting"
          >
            {{ $t('Apply Override') }}
          </WdsButton>
        </WdsStack>

        <input type="hidden" name="determination_result" :value="overrideResult" />
        <input type="hidden" name="override_reason" :value="overrideReason" />
        <input type="hidden" name="override_accept" :value="overrideAccept ? '1' : '0'" />
        <input type="hidden" name="contract_signature" :value="signatureValue" />
        <input type="hidden" name="_token" :value="csrfToken" />
        <input type="hidden" name="_method" value="PATCH" />
      </form>
    </template>
  </WdsModal>
</template>

<script lang="ts" setup>
import { ref, computed } from 'vue'
import {
  WdsAlert,
  WdsButton,
  WdsCheckbox,
  WdsInputArea,
  WdsModal,
  WdsSelect,
  WdsStack,
  WdsText,
} from '@worksome/wds-components'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import ContractFormSignature from '@/components/ContractFormSignature.vue'

interface Props {
  classification: any
  hireNumber: string
  csrfToken: string
}

const props = defineProps<Props>()

defineEmits<{
  close: []
}>()

// State
const overrideResult = ref('outside')
const overrideReason = ref('')
const overrideAccept = ref(false)
const submitting = ref(false)
const signatureComponent = ref()
const signatureValue = ref('')

const overrideResultSelect = computed({
  get: () => ({ value: overrideResult.value, label: $t('Independent Contractor') }),
  set: (option) => {
    overrideResult.value = option?.value || 'outside'
  },
})

const overrideUrl = computed(() => `/hires/${props.hireNumber}/override-sds`)

const isIndependentClassification = computed(
  () => props.classification.result.outcome === 'outside',
)

const canSubmit = computed(() => {
  if (isIndependentClassification.value) return true
  return overrideReason.value.trim() && overrideAccept.value
})

const handleSubmit = (event: Event) => {
  if (!canSubmit.value) {
    event.preventDefault()
    return
  }

  if (signatureComponent.value) {
    signatureValue.value =
      signatureComponent.value.getSignature?.() || signatureComponent.value.signature || ''
  }

  submitting.value = true
  // Let form submit naturally - it will redirect/reload the page
}
</script>
